import { lazy, Suspense, type JSX } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { useSilentRefresh } from '@/features/auth/useSilentRefresh';

// Lazy-loaded route components — Vite emits a separate JS chunk per import(),
// so only the code for the current screen is downloaded on first navigation.
const LoginPassword = lazy(() =>
  import('@/features/auth/LoginPassword').then((m) => ({ default: m.LoginPassword })),
);
const LoginMobile = lazy(() =>
  import('@/features/auth/LoginMobile').then((m) => ({ default: m.LoginMobile })),
);
const LoginOtp = lazy(() =>
  import('@/features/auth/LoginOtp').then((m) => ({ default: m.LoginOtp })),
);
const ClubPicker = lazy(() =>
  import('@/features/clubs/ClubPicker').then((m) => ({ default: m.ClubPicker })),
);
const ClubEmpty = lazy(() =>
  import('@/features/clubs/ClubEmpty').then((m) => ({ default: m.ClubEmpty })),
);
const ClubRegister = lazy(() =>
  import('@/features/clubs/ClubRegister').then((m) => ({ default: m.ClubRegister })),
);
const ClubCreated = lazy(() =>
  import('@/features/clubs/ClubCreated').then((m) => ({ default: m.ClubCreated })),
);
const ClubJoinPreview = lazy(() =>
  import('@/features/clubs/ClubJoinPreview').then((m) => ({ default: m.ClubJoinPreview })),
);
const ClubHome = lazy(() =>
  import('@/features/clubs/ClubHome').then((m) => ({ default: m.ClubHome })),
);
const ClubSettingsScreen = lazy(() =>
  import('@/features/clubs/ClubSettingsScreen').then((m) => ({ default: m.ClubSettingsScreen })),
);
const ClubStatsScreen = lazy(() =>
  import('@/features/clubs/ClubStatsScreen').then((m) => ({ default: m.ClubStatsScreen })),
);
const TreasuryScreen = lazy(() =>
  import('@/features/clubs/TreasuryScreen').then((m) => ({ default: m.TreasuryScreen })),
);
const TeamsScreen = lazy(() =>
  import('@/features/teams/TeamsScreen').then((m) => ({ default: m.TeamsScreen })),
);
const CreateTeamScreen = lazy(() =>
  import('@/features/teams/CreateTeamScreen').then((m) => ({ default: m.CreateTeamScreen })),
);
const TeamRosterScreen = lazy(() =>
  import('@/features/teams/TeamRosterScreen').then((m) => ({ default: m.TeamRosterScreen })),
);
const WizardStep1Teams = lazy(() =>
  import('@/features/matches/WizardStep1Teams').then((m) => ({ default: m.WizardStep1Teams })),
);
const WizardStep2Xi = lazy(() =>
  import('@/features/matches/WizardStep2Xi').then((m) => ({ default: m.WizardStep2Xi })),
);
const WizardStep3FormatToss = lazy(() =>
  import('@/features/matches/WizardStep3FormatToss').then((m) => ({
    default: m.WizardStep3FormatToss,
  })),
);
const ClubMatchesList = lazy(() =>
  import('@/features/matches/ClubMatchesList').then((m) => ({ default: m.ClubMatchesList })),
);
const ScorerSetup = lazy(() =>
  import('@/features/scoring/ScorerSetup').then((m) => ({ default: m.ScorerSetup })),
);
const ScorerScreen = lazy(() =>
  import('@/features/scoring/ScorerScreen').then((m) => ({ default: m.ScorerScreen })),
);
const SpectatorScreen = lazy(() =>
  import('@/features/spectator/SpectatorScreen').then((m) => ({ default: m.SpectatorScreen })),
);
const PublicLiveScreen = lazy(() =>
  import('@/features/spectator/PublicLiveScreen').then((m) => ({ default: m.PublicLiveScreen })),
);
const PlayerProfileScreen = lazy(() =>
  import('@/features/players/PlayerProfileScreen').then((m) => ({
    default: m.PlayerProfileScreen,
  })),
);
const ShareScreen = lazy(() =>
  import('@/features/share/ShareScreen').then((m) => ({ default: m.ShareScreen })),
);
const ComingSoon = lazy(() =>
  import('@/components/ComingSoon').then((m) => ({ default: m.ComingSoon })),
);

function PageSpinner(): JSX.Element {
  return (
    <main className="min-h-screen bg-bg-0 flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-white/15 border-t-emerald-400 rounded-full animate-spin" />
    </main>
  );
}

/**
 * Routing tree. All feature routes are code-split via React.lazy so only the
 * chunk for the current screen is downloaded on first navigation.
 */
export function App(): JSX.Element {
  useSilentRefresh();
  return (
    <BrowserRouter>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          <Route path="/login" element={<LoginPassword />} />
          <Route path="/login/otp" element={<LoginMobile />} />
          <Route path="/login/otp/verify" element={<LoginOtp />} />

          {/* Public, no-login live scorecard — destination of the scan-to-watch QR */}
          <Route path="/spectate/:matchId" element={<PublicLiveScreen />} />

          {/* All routes below require auth */}
          <Route
            path="/clubs"
            element={
              <RequireAuth>
                <ClubPicker />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/empty"
            element={
              <RequireAuth>
                <ClubEmpty />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/new"
            element={
              <RequireAuth>
                <ClubRegister />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/created/:clubId"
            element={
              <RequireAuth>
                <ClubCreated />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/join/:code"
            element={
              <RequireAuth>
                <ClubJoinPreview />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/:clubId"
            element={
              <RequireAuth>
                <ClubHome />
              </RequireAuth>
            }
          />

          {/* Club's Matches tab */}
          <Route
            path="/clubs/:clubId/matches"
            element={
              <RequireAuth>
                <ClubMatchesList />
              </RequireAuth>
            }
          />

          {/* Match-creation wizard (Phase 3) — 3 steps */}
          <Route
            path="/clubs/:clubId/matches/new"
            element={
              <RequireAuth>
                <WizardStep1Teams />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/:clubId/matches/new/xi"
            element={
              <RequireAuth>
                <WizardStep2Xi />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/:clubId/matches/new/format"
            element={
              <RequireAuth>
                <WizardStep3FormatToss />
              </RequireAuth>
            }
          />

          {/* Scoring (Phase 4) */}
          <Route
            path="/matches/:matchId/setup"
            element={
              <RequireAuth>
                <ScorerSetup />
              </RequireAuth>
            }
          />
          <Route
            path="/matches/:matchId/score"
            element={
              <RequireAuth>
                <ScorerScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/matches/:matchId/live"
            element={
              <RequireAuth>
                <SpectatorScreen />
              </RequireAuth>
            }
          />

          {/* Player profile (Phase 6) */}
          <Route
            path="/players/:playerId/profile"
            element={
              <RequireAuth>
                <PlayerProfileScreen />
              </RequireAuth>
            }
          />

          {/* Share (Phase 7) */}
          <Route
            path="/matches/:matchId/share"
            element={
              <RequireAuth>
                <ShareScreen />
              </RequireAuth>
            }
          />

          {/* Club Stats — overview + batting/bowling leaderboards */}
          <Route
            path="/clubs/:clubId/stats"
            element={
              <RequireAuth>
                <ClubStatsScreen />
              </RequireAuth>
            }
          />

          {/* Club Settings — admin: club display image */}
          <Route
            path="/clubs/:clubId/settings"
            element={
              <RequireAuth>
                <ClubSettingsScreen />
              </RequireAuth>
            }
          />

          {/* Club Treasury — manual dues ledger */}
          <Route
            path="/clubs/:clubId/treasury"
            element={
              <RequireAuth>
                <TreasuryScreen />
              </RequireAuth>
            }
          />
          {/* Club Teams — list, create, and per-team roster management */}
          <Route
            path="/clubs/:clubId/teams"
            element={
              <RequireAuth>
                <TeamsScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/:clubId/teams/new"
            element={
              <RequireAuth>
                <CreateTeamScreen />
              </RequireAuth>
            }
          />
          <Route
            path="/clubs/:clubId/teams/:teamId"
            element={
              <RequireAuth>
                <TeamRosterScreen />
              </RequireAuth>
            }
          />

          {/* User profile (coming soon) */}
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ComingSoon title="Profile" />
              </RequireAuth>
            }
          />

          {/* Default — go to club picker */}
          <Route
            path="/*"
            element={
              <RequireAuth>
                <ClubPicker />
              </RequireAuth>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
